import { useState, useCallback, useEffect, useRef } from 'react';
import { triggerFocusEvent } from './useFocusEvent';

/**
 * Custom hook to manage line items in an invoice
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.invoiceId - ID of the invoice
 * @param {string} options.invoiceNumber - Invoice number
 * @param {Function} options.onUpdate - Callback to update parent component
 * @param {Array} options.initialItems - Initial line items
 * @returns {Object} - Line item management methods and state
 */
const useLineItemManagement = ({ invoiceId, invoiceNumber, onUpdate, initialItems = [] }) => {
  // Use a ref to track the next item ID, starting from 0
  // This will be incremented for each new item and never reused
  const nextItemIdRef = useRef(0);
  
  // Initialize line items with sequential IDs if they don't have them
  const initialItemsWithIds = initialItems.map(item => {
    if (item.id === undefined) {
      const newId = nextItemIdRef.current++;
      return { ...item, id: newId };
    }
    // If the item already has an ID, make sure our counter stays ahead
    if (typeof item.id === 'number' && item.id >= nextItemIdRef.current) {
      nextItemIdRef.current = item.id + 1;
    }
    return item;
  });
  
  const [lineItems, setLineItems] = useState(initialItemsWithIds);
  const [newestItemId, setNewestItemId] = useState(null);
  const [focusedItemId, setFocusedItemId] = useState(null);
  const [resetPoppers, setResetPoppers] = useState(false);
  // Add a new state to track duplicated items that should skip the focus flow
  const [duplicatedItemId, setDuplicatedItemId] = useState(null);
  // Add a new state to track items created with Shift+Enter
  const [hotKeyCreatedItemId, setHotKeyCreatedItemId] = useState(null);
  
  // Update parent when line items change
  useEffect(() => {
    if (onUpdate) {
      const totalQuantity = lineItems.reduce((sum, item) => sum + (item.totalQuantity || 0), 0);
      
      onUpdate(invoiceId, {
        invoiceNumber,
        items: lineItems,
        totalQuantity
      });
    }
  }, [invoiceId, invoiceNumber, lineItems, onUpdate]);
  
  // Add a new line item
  const handleAddNewLineItem = useCallback(() => {
    // Get the next sequential ID
    const newId = nextItemIdRef.current++;
    
    // Create a new empty line item
    const newLineItem = {
      id: newId,
      imgUrl: null,
      productUrl: null,
      style: "",
      brand: "",
      color: "",
      description: "",
      sizes: {},
      totalQuantity: 0
    };
    
    // Add the new line item to the list
    const updatedLineItems = [...lineItems, newLineItem];
    setLineItems(updatedLineItems);
    setNewestItemId(newLineItem.id);
    // Clear any duplicated item ID
    setDuplicatedItemId(null);
    // Clear any hotkey created item ID
    setHotKeyCreatedItemId(null);
    
    return newLineItem;
  }, [lineItems]);
  
  // Add a new line item below the specified item
  const handleAddNewLineItemBelow = useCallback((item) => {
    // Use the focused item ID if available, otherwise use the provided item ID
    const targetItemId = focusedItemId || item.id;
    
    // Find the index of the target item
    const itemIndex = lineItems.findIndex(lineItem => lineItem.id === targetItemId);
    
    if (itemIndex !== -1) {
      // Get the next sequential ID
      const newId = nextItemIdRef.current++;
      
      // Create a new empty line item
      const newLineItem = {
        id: newId,
        imgUrl: null,
        productUrl: null,
        style: "",
        brand: "",
        color: "",
        description: "",
        sizes: {},
        totalQuantity: 0
      };
      
      // Insert the new line item after the specified item
      const updatedLineItems = [
        ...lineItems.slice(0, itemIndex + 1), // Items before and including the specified item
        newLineItem, // Insert the new item
        ...lineItems.slice(itemIndex + 1) // Items after the specified item
      ];
      
      // Update the state with the new array
      setLineItems(updatedLineItems);
      
      // Set the newest item ID to trigger focus
      setNewestItemId(newLineItem.id);
      
      // Set the hotkey created item ID
      setHotKeyCreatedItemId(newLineItem.id);
      
      return newLineItem;
    }
    
    return null;
  }, [lineItems, focusedItemId]);
  
  // Remove a line item
  const handleRemoveLineItem = useCallback((itemId) => {
    const updatedLineItems = lineItems.filter(item => item.id !== itemId);
    setLineItems(updatedLineItems);
  }, [lineItems]);
  
  // Duplicate a line item
  const handleDuplicateLineItem = useCallback((itemToDuplicate) => {
    if (!itemToDuplicate) return null;
    
    // Get the next sequential ID
    const newId = nextItemIdRef.current++;
    
    // Create a deep copy of the item with a new ID
    const duplicatedItem = {
      ...JSON.parse(JSON.stringify(itemToDuplicate)),
      id: newId
    };
    
    // Find the index of the original item
    const originalItemIndex = lineItems.findIndex(item => item.id === itemToDuplicate.id);
    
    // Create a new array with the duplicated item inserted after the original
    const updatedLineItems = [
      ...lineItems.slice(0, originalItemIndex + 1), // Items before and including the original
      duplicatedItem, // Insert the duplicate
      ...lineItems.slice(originalItemIndex + 1) // Items after the original
    ];
    
    // Update the state with the new array
    setLineItems(updatedLineItems);
    
    // Set the duplicated item ID to skip the focus flow
    setDuplicatedItemId(duplicatedItem.id);
    
    // We don't set newestItemId here because we don't want to trigger the focus flow
    
    return duplicatedItem;
  }, [lineItems]);
  
  // Update a line item
  const handleUpdateLineItem = useCallback((updatedItem) => {
    const updatedLineItems = lineItems.map(item => 
      item.id === updatedItem.id ? { ...item, ...updatedItem } : item
    );
    
    setLineItems(updatedLineItems);
  }, [lineItems]);
  
  // Handle item focus
  const handleItemFocus = useCallback((itemId) => {
    setFocusedItemId(itemId);
  }, []);
  
  // Reset newest item ID after it's been processed
  useEffect(() => {
    if (newestItemId) {
      // Reset after a short delay to ensure the component has rendered
      const timer = setTimeout(() => {
        setNewestItemId(null);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [newestItemId]);
  
  // Reset duplicated item ID after it's been processed
  useEffect(() => {
    if (duplicatedItemId) {
      // Reset after a short delay to ensure the component has rendered
      const timer = setTimeout(() => {
        setDuplicatedItemId(null);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [duplicatedItemId]);
  
  // Reset hotkey created item ID after it's been processed
  useEffect(() => {
    if (hotKeyCreatedItemId) {
      // Reset after a short delay to ensure the component has rendered
      const timer = setTimeout(() => {
        setHotKeyCreatedItemId(null);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [hotKeyCreatedItemId]);
  
  // Auto-create first item if needed
  useEffect(() => {
    // If there are no line items, create one automatically
    if (lineItems.length === 0) {
      handleAddNewLineItem();
    }
  }, [lineItems.length, handleAddNewLineItem]);
  
  return {
    lineItems,
    newestItemId,
    duplicatedItemId,
    hotKeyCreatedItemId,
    focusedItemId,
    resetPoppers,
    handleAddNewLineItem,
    handleAddNewLineItemBelow,
    handleRemoveLineItem,
    handleDuplicateLineItem,
    handleUpdateLineItem,
    handleItemFocus
  };
};

export default useLineItemManagement; 